import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';

export type SearchContainerType = 'page' | 'database';

export type SearchResult = {
  blockId: string;
  title: string;
  type: string;
  parentId: string | null;
  containerId: string;
  containerType: SearchContainerType;
  containerTitle: string;
  snippet: string;
  score: number;
  matchedVia: ('fts' | 'trigram' | 'semantic')[];
};

type RawSearchResult = Omit<SearchResult, 'containerId' | 'containerType' | 'containerTitle'> & {
  containerId?: string;
  containerType?: SearchContainerType;
  containerTitle?: string;
};

/** Back-compat when API predates container fields (pre-deploy). */
export function normalizeSearchResult(row: RawSearchResult): SearchResult {
  if (row.containerId && row.containerType) {
    return {
      ...row,
      containerId: row.containerId,
      containerType: row.containerType,
      containerTitle: row.containerTitle?.trim() || row.title,
    };
  }

  if (row.type === 'page' || row.type === 'database') {
    return {
      ...row,
      containerId: row.blockId,
      containerType: row.type,
      containerTitle: row.title,
    };
  }

  return {
    ...row,
    containerId: row.parentId ?? row.blockId,
    containerType: 'page',
    containerTitle: row.title,
  };
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeaders(),
      ...(await getNativeBearerAuthHeaders()),
      ...init?.headers,
    },
  });
}

export async function searchBlocks(input: {
  query: string;
  limit?: number;
  scopeRootIds?: string[];
}): Promise<SearchResult[]> {
  const res = await apiFetch('/rpc/search', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Search failed (${res.status})`);
  }
  const data = (await res.json()) as { results: RawSearchResult[] };
  return data.results.map(normalizeSearchResult);
}
